import React, { useState, useRef, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import api from '../../../api/axios';
import { agentServiceApi } from '../../../api/axios';
import AgentLoadingScreen from './AgentLoadingScreen';

const LANGUAGES = [
  'English', 'Hindi', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Marathi',
  'Bengali', 'Gujarati', 'Punjabi', 'Urdu', 'Odia', 'French', 'Spanish',
  'German', 'Arabic', 'Portuguese', 'Japanese', 'Chinese (Mandarin)',
];

const ENABLED_LANGUAGES = ['English', 'Hindi'];

const VOICE_COLORS = [
  'from-pink-400 to-rose-500',
  'from-violet-400 to-purple-500',
  'from-blue-400 to-indigo-500',
  'from-gray-500 to-slate-600',
  'from-teal-400 to-emerald-500',
  'from-orange-400 to-amber-500',
  'from-cyan-400 to-sky-500',
  'from-lime-400 to-green-500',
  'from-fuchsia-400 to-pink-500',
  'from-amber-400 to-yellow-500',
];

const KB_SECTIONS = [
  { id: 'shipping', label: 'Shipping Info', icon: 'Truck' },
  { id: 'product', label: 'Product Info', icon: 'Package' },
  { id: 'company', label: 'About Company', icon: 'Building2' },
  { id: 'refund', label: 'Return / Refund Policy', icon: 'RotateCcw' },
  { id: 'contact', label: 'Contact Us', icon: 'Phone' },
];


const EnterpriseContactPopup = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center">
              <Icon name="Crown" size={16} className="text-white" />
            </div>
            <h3 className="text-base font-bold text-gray-900">Upgrade to Enterprise</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={16} className="text-gray-500" />
          </button>
        </div>
        <p className="text-sm text-gray-600">
          Unlock all 19 languages including Tamil, Telugu, Kannada, French, Spanish, and more with our Enterprise plan.
        </p>
        <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Mail" size={14} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Email Us</p>
              <a href="mailto:business@calltify.com" className="text-sm font-medium text-violet-700 hover:underline">business@calltify.com</a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Phone" size={14} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Call Us</p>
              <a href="tel:+919289016406" className="text-sm font-medium text-violet-700 hover:underline">+91-9289016406</a>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center flex-shrink-0">
              <Icon name="Globe" size={14} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xs text-gray-500">Website</p>
              <a href="https://calltify.com" target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-violet-700 hover:underline">calltify.com</a>
            </div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="w-full px-4 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-violet-700 hover:to-indigo-700 transition-all"
        >
          Got it
        </button>
      </div>
    </div>
  );
};

const SearchableDropdown = ({ label, options, value, onChange, placeholder, enabledOptions, onLockedClick }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filtered = options?.filter((o) => o?.toLowerCase()?.includes(query?.toLowerCase()));

  const handleSelect = (opt) => {
    onChange(opt);
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}</label>
      <div
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-white border border-gray-200 rounded-xl cursor-pointer hover:border-violet-300 transition-colors"
        onClick={() => setOpen((p) => !p)}
      >
        <span className={`flex-1 text-sm ${value ? 'text-gray-800' : 'text-gray-400'}`}>
          {value || placeholder}
        </span>
        <Icon name="ChevronDown" size={16} className={`text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 rounded-lg">
              <Icon name="Search" size={14} className="text-gray-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e?.target?.value)}
                placeholder="Search..."
                className="flex-1 text-sm bg-transparent outline-none text-gray-700 placeholder:text-gray-400"
              />
            </div>
          </div>
          <div className="max-h-44 overflow-y-auto">
            {filtered?.map((opt) => {
              const isEnabled = !enabledOptions || enabledOptions.includes(opt);
              return (
                <button
                  key={opt}
                  onClick={() => isEnabled ? handleSelect(opt) : onLockedClick?.()}
                  className={`w-full flex items-center justify-between px-4 py-2 text-sm transition-colors ${
                    !isEnabled
                      ? 'text-gray-300 cursor-pointer hover:bg-gray-50'
                      : value === opt
                        ? 'bg-violet-50 text-violet-700 font-medium'
                        : 'text-gray-700 hover:bg-violet-50 hover:text-violet-700'
                  }`}
                >
                  <span>{opt}</span>
                  {!isEnabled && (
                    <span className="text-[10px] font-semibold text-violet-500 bg-violet-50 px-1.5 py-0.5 rounded-full whitespace-nowrap">Upgrade to Enterprise</span>
                  )}
                </button>
              );
            })}
            {filtered?.length === 0 && (
              <p className="px-4 py-3 text-sm text-gray-400 text-center">No results found</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Map voice API provider names to agent-service accepted enum values
const PROVIDER_MAP = {
  'smallest_ai': 'smallest',
};

const LANG_CODE_MAP = {
  'English': 'en', 'Hindi': 'hi', 'Tamil': 'ta', 'Telugu': 'te', 'Kannada': 'kn',
  'Malayalam': 'ml', 'Marathi': 'mr', 'Bengali': 'bn', 'Gujarati': 'gu', 'Punjabi': 'pa',
  'Urdu': 'ur', 'Odia': 'or', 'French': 'fr', 'Spanish': 'es', 'German': 'de',
  'Arabic': 'ar', 'Portuguese': 'pt', 'Japanese': 'ja', 'Chinese (Mandarin)': 'zh',
};

const AssistantCreationFlow = ({ isOpen, onClose, template, onAgentCreated, onCreationComplete }) => {
  const [step, setStep] = useState(1);
  const [playingVoice, setPlayingVoice] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState(null);
  const [showLoading, setShowLoading] = useState(false);
  const [createdAgentName, setCreatedAgentName] = useState('');
  const [createdAgentId, setCreatedAgentId] = useState(null);
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);
  const [showEnterprisePopup, setShowEnterprisePopup] = useState(false);
  const audioRef = useRef(null);

  // Fetch voices from API
  useEffect(() => {
    if (!isOpen) return;
    const fetchVoices = async () => {
      setVoicesLoading(true);
      try {
        const res = await agentServiceApi.get('/voices');
        const voiceList = res.data?.voices || res.data?.data?.voices || [];
        const mapped = voiceList
          .filter(v => v.is_active !== false)
          .map((v, i) => ({
            id: v.voice_id,
            name: v.display_name || v.voice_id,
            gender: v.gender || '—',
            accent: v.accent || '—',
            color: VOICE_COLORS[i % VOICE_COLORS.length],
            sampleUrl: v.sample_audio_url || null,
            model: v.model || '',
            provider: v.provider || '',
          }));
        setVoices(mapped);
        if (mapped.length > 0 && !selectedVoice) {
          setSelectedVoice(mapped[0].id);
        }
      } catch (err) {
        console.error('Failed to fetch voices:', err);
      } finally {
        setVoicesLoading(false);
      }
    };
    fetchVoices();
  }, [isOpen]);

  // Step 1
  const [agentName, setAgentName] = useState(template?.name || '');
  const [primaryLang, setPrimaryLang] = useState('English');
  const [secondaryLang, setSecondaryLang] = useState('');
  const [selectedVoice, setSelectedVoice] = useState(null);

  // Step 2
  const [companyName, setCompanyName] = useState('');
  const [callPurpose, setCallPurpose] = useState(template?.callPurpose || '');
  const [additionalInfo, setAdditionalInfo] = useState('');
  const [variables, setVariables] = useState(['callee_name', 'mobile_number']);
  const [newVar, setNewVar] = useState('');
  const [sectionFiles, setSectionFiles] = useState({}); // { sectionId: [{ name, file }, ...] }
  const [uploadingSectionId, setUploadingSectionId] = useState(null);
  const [activeKBSections, setActiveKBSections] = useState([]);
  // Step 3 — OTP Verification
  const [mobileNumber, setMobileNumber] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const otpRefs = useRef([]);

  const fileInputRef = useRef(null);
  const createAgentRef = useRef(null);

  // Sync state when template prop changes
  useEffect(() => {
    setCallPurpose(template?.callPurpose || '');
  }, [template]);

  // Check if phone is already verified
  useEffect(() => {
    if (!isOpen) return;
    api.get('/api/v1/otp/status')
      .then((res) => {
        const data = res.data?.data;
        if (data?.isPhoneVerified) {
          setOtpVerified(true);
          setMobileNumber(data?.phone || '');
          setOtpSent(true);
        }
      })
      .catch(() => {});
  }, [isOpen]);

  if (!isOpen) return null;

  const handleAddVar = () => {
    const trimmed = newVar?.trim()?.replace(/\s+/g, '_')?.toLowerCase();
    if (trimmed && !variables?.includes(trimmed)) {
      setVariables((p) => [...p, trimmed]);
      setNewVar('');
    }
  };

  const handleRemoveVar = (v) => {
    if (v === 'callee_name' || v === 'mobile_number') return;
    setVariables((p) => p?.filter((x) => x !== v));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e?.target?.files || []);
    if (files.length === 0 || !uploadingSectionId) return;
    const sectionId = uploadingSectionId;
    setUploadingSectionId(null);
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Add entries with 'uploading' status immediately
    const entries = files.map((f) => ({ name: f.name, file: f, status: 'uploading', publicUrl: null }));
    setSectionFiles((p) => ({ ...p, [sectionId]: [...(p[sectionId] || []), ...entries] }));

    // Upload each file to S3 in parallel
    const startIndex = (sectionFiles[sectionId] || []).length; // offset for new entries
    await Promise.all(
      entries.map(async (entry, i) => {
        const idx = startIndex + i;
        try {
          const presignRes = await agentServiceApi.post('/storage/presigned-url', {
            file_name: entry.name,
            section: sectionId,
          });
          const { upload_url, public_url } = presignRes.data;
          await fetch(upload_url, {
            method: 'PUT',
            body: entry.file,
            headers: { 'Content-Type': entry.file.type || 'application/octet-stream' },
          });
          setSectionFiles((p) => {
            const arr = [...(p[sectionId] || [])];
            arr[idx] = { ...arr[idx], status: 'uploaded', publicUrl: public_url };
            return { ...p, [sectionId]: arr };
          });
        } catch (err) {
          console.error(`Upload failed for ${entry.name}:`, err);
          setSectionFiles((p) => {
            const arr = [...(p[sectionId] || [])];
            arr[idx] = { ...arr[idx], status: 'error' };
            return { ...p, [sectionId]: arr };
          });
        }
      })
    );
  };

  const toggleKBSection = (id) => {
    setActiveKBSections((p) => {
      if (p?.includes(id)) {
        // Deselecting — also remove uploaded file for this section
        setSectionFiles((f) => { const n = {...f}; delete n[id]; return n; });
        return p?.filter((x) => x !== id);
      }
      return [...p, id];
    });
  };

  const handleCreate = async () => {
    setCreating(true);
    setCreateError(null);

    // Show loading screen immediately
    setCreatedAgentName(agentName);
    setShowLoading(true);

    const apiPromise = (async () => {
      const selectedVoiceObj = voices.find(v => v.id === selectedVoice);
      const voiceName = selectedVoiceObj?.id || 'aria';
      const langCode = LANG_CODE_MAP[primaryLang] || 'en';

      const kbItems = [];
      Object.entries(sectionFiles).forEach(([sectionId, files]) => {
        files.forEach(f => {
          if (f.status === 'uploaded' && f.publicUrl) {
            kbItems.push({ url: f.publicUrl, section: sectionId, name: f.name });
          }
        });
      });

      const langItems = [{ code: langCode, label: primaryLang, is_primary: true }];
      if (secondaryLang) {
        const secCode = LANG_CODE_MAP[secondaryLang] || secondaryLang.toLowerCase().slice(0, 2);
        langItems.push({ code: secCode, label: secondaryLang, is_primary: false });
      }

      const agentPayload = {
        name: agentName,
        company_name: companyName,
        purpose: callPurpose,
        additional_info: additionalInfo || undefined,
        variables: variables.map(v => ({ name: v })),
        kbs: kbItems.length > 0 ? kbItems : undefined,
        languages: langItems,
        stt_config: { provider: 'soniox', model: 'stt-rt-v5', language: langCode, diarization: false, context_text: null, context_terms: null, max_endpoint_delay_ms: null },
        llm_config: { model: 'gpt-4.1-mini', provider: 'openai', max_tokens: 300, temperature: 0.4, presence_penalty: 0.2, reasoning_effort: null, frequency_penalty: 0.4 },
        tts_config: { model: 'lightning_v3.1_pro', speed: 0.9, style: null, voice: 'zariya', volume: 0.75, language: langCode, provider: 'smallest', smoothing: 0.55, stability: 0.5, speaker_boost: false, similarity_boost: 0.75 },
        vad_config: { provider: 'silero', threshold: 0.5, min_speech: 0.1, min_silence: 0.3 },
        endpointing_config: { max_delay: 0.8, min_delay: 0.25, preemptive_tts: false, turn_detection: 'vad', preemptive_generation: false },
        interruption_config: { enabled: true, min_words: 2, min_duration: 0.4 },
        session_config: { max_duration: 300, greeting_delay: 1.0, inactivity_timeout: 4.0, user_online_enabled: true, user_online_message: 'Hello', max_inactivity_nudges: 3, silence_hangup_enabled: true, silence_hangup_timeout: 10.0 },
        audio_config: { nc_mode: 'telephony', bns_type: 'office', agc_enabled: true, bns_enabled: true, thinking_sound: false, voicemail_detection: true },
        telephony_config: { provider: 'plivo' },
      };

      const agentRes = await agentServiceApi.post('/agents', agentPayload);
      const agentId = agentRes.data?.agent_id || agentRes.data?.data?.agent_id;

      await api.post('/api/v1/agent/add', {
        platform: 'calltify',
        platformAgentId: agentId,
        name: agentName,
        isActive: true,
      });

      const allFiles = Object.entries(sectionFiles);
      for (const [sectionId, files] of allFiles) {
        const sectionLabel = KB_SECTIONS.find((s) => s.id === sectionId)?.label || sectionId;
        for (const entry of files) {
          if (entry.status !== 'uploaded' || !entry.publicUrl) continue;
          try {
            await api.post('/api/v1/dashboard/create-knowledge-base', null, {
              params: {
                knowledgeBaseType: 'DOC',
                knowledgeBaseName: `${sectionLabel} - ${entry.name}`,
                url: entry.publicUrl,
                agentIds: agentId,
              },
            });
          } catch (kbErr) {
            console.error(`KB registration failed for ${entry.name}:`, kbErr);
          }
        }
      }

      if (typeof fbq === 'function') fbq('track', 'Lead', { content_name: 'AgentCreated', content_category: 'AgentCreationFlow' });
      if (typeof fbq === 'function') fbq('trackCustom', 'review-create-ai-assistant-clicked', { content_category: 'AgentCreationFlow' });
      window.dataLayer?.push({ event: 'agent_created' });
      window.dataLayer?.push({ event: 'review_create_ai_assistant_clicked' });

      setCreatedAgentId(agentId);
      return agentId;
    })();

    createAgentRef.current = apiPromise;

    try {
      await apiPromise;
    } catch (err) {
      console.error('Create agent error:', err);
      setCreateError(err.response?.data?.message || err.message || 'Failed to create agent');
      setShowLoading(false);
    } finally {
      setCreating(false);
    }
  };

  const handleSendOtp = async () => {
    if (mobileNumber?.trim()?.length < 10) return;
    setOtpLoading(true);
    setOtpError('');
    try {
      await api.post('/api/v1/otp/send', { phone: mobileNumber });
      setOtpSent(true);
      if (typeof fbq === 'function') fbq('trackCustom', 'otp-send-clicked', { content_category: 'OtpVerification' });
      window.dataLayer?.push({ event: 'otp_send_clicked' });
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to send OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError('');
    setOtp(['', '', '', '', '', '']);
    try {
      await api.post('/api/v1/otp/resend', { phone: mobileNumber });
      if (typeof fbq === 'function') fbq('trackCustom', 'otp-resend-clicked', { content_category: 'OtpVerification' });
      window.dataLayer?.push({ event: 'otp_resend_clicked' });
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Failed to resend OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async (otpValue) => {
    setOtpLoading(true);
    setOtpError('');
    try {
      const res = await api.post('/api/v1/otp/verify', { phone: mobileNumber, otp: otpValue });
      const data = res.data?.data;
      if (data?.verified) {
        setOtpVerified(true);
        if (typeof fbq === 'function') fbq('trackCustom', 'otp-verified', { content_category: 'OtpVerification' });
        window.dataLayer?.push({ event: 'otp_verified' });
        // Auto-create agent after OTP verification
        handleCreate();
      } else {
        setOtpError(data?.message || 'Invalid OTP');
        if (typeof fbq === 'function') fbq('trackCustom', 'otp-verification-failed', { content_category: 'OtpVerification' });
        window.dataLayer?.push({ event: 'otp_verification_failed' });
      }
    } catch (err) {
      setOtpError(err.response?.data?.message || 'Verification failed');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value?.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) {
      otpRefs?.current?.[index + 1]?.focus();
    }
    // Auto-verify when all 6 digits entered
    if (value && index === 5) {
      const fullOtp = [...newOtp].join('');
      if (fullOtp.length === 6) {
        handleVerifyOtp(fullOtp);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e?.key === 'Backspace' && !otp?.[index] && index > 0) {
      otpRefs?.current?.[index - 1]?.focus();
    }
  };

  const resetForm = () => {
    setStep(1);
    setAgentName(template?.name || '');
    setPrimaryLang('English');
    setSecondaryLang('');
    setSelectedVoice(null);
    setCompanyName('');
    setCallPurpose(template?.callPurpose || '');
    setAdditionalInfo('');
    setVariables(['callee_name', 'mobile_number']);
    setNewVar('');
    setSectionFiles({});
    setActiveKBSections([]);
    setCreating(false);
    setCreateError(null);
    setCreatedAgentName('');
    setCreatedAgentId(null);
    setMobileNumber('');
    setOtpSent(false);
    setOtpVerified(false);
    setOtp(['', '', '', '', '', '']);
    setOtpLoading(false);
    setOtpError('');
  };

  const handleLoadingComplete = async () => {
    // Wait for API to finish if animation completed first
    let agentId = createdAgentId;
    if (createAgentRef.current) {
      try {
        agentId = await createAgentRef.current;
      } catch {
        // Error already handled in handleCreate
      }
    }
    const completionData = { agentId: agentId, agentName: createdAgentName, primaryLang, selectedVoice, companyName };
    setShowLoading(false);
    resetForm();
    onAgentCreated?.();
    onClose();
    onCreationComplete?.(completionData);
  };

  const STEPS = otpVerified ? ['Agent Basic', 'Call Context'] : ['Agent Basic', 'Call Context', 'Verify'];

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden w-full h-full sm:w-[68vw] sm:h-[85vh] sm:max-w-[1100px]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-600 flex items-center justify-center flex-shrink-0">
              <Icon name="Bot" size={18} className="text-white" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                {template ? `Using: ${template?.name}` : 'Create AI Assistant'}
              </h2>
              <p className="text-xs text-gray-500 hidden sm:block">Configure your assistant step by step</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors">
            <Icon name="X" size={18} className="text-gray-500" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center gap-0 px-4 sm:px-6 py-3 bg-gray-50 border-b border-gray-100 flex-shrink-0">
          {STEPS?.map((s, i) => {
            const num = i + 1;
            const isActive = step === num;
            const isDone = step > num;
            return (
              <React.Fragment key={s}>
                <div className="flex items-center gap-1.5 sm:gap-2">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all flex-shrink-0 ${
                    isDone ? 'bg-violet-600 text-white' : isActive ? 'bg-violet-600 text-white ring-4 ring-violet-100' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {isDone ? <Icon name="Check" size={13} /> : num}
                  </div>
                  <span className={`text-xs sm:text-sm font-medium hidden sm:inline ${isActive ? 'text-violet-700' : isDone ? 'text-gray-600' : 'text-gray-400'}`}>{s}</span>
                </div>
                {i < STEPS?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-2 sm:mx-3 rounded-full ${step > num ? 'bg-violet-400' : 'bg-gray-200'}`} />
                )}
              </React.Fragment>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
          {/* ===== STEP 1 ===== */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon name="User" size={13} className="text-violet-600" />
                  </div>
                  Agent Basic
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Agent Name <span className="text-red-500">*</span></label>
                    <input
                      value={agentName}
                      onChange={(e) => setAgentName(e?.target?.value)}
                      placeholder="e.g. Ruchi"
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <SearchableDropdown
                    label="Primary Language *"
                    options={LANGUAGES}
                    value={primaryLang}
                    onChange={setPrimaryLang}
                    placeholder="Select language"
                    enabledOptions={ENABLED_LANGUAGES}
                    onLockedClick={() => setShowEnterprisePopup(true)}
                  />
                  <SearchableDropdown
                    label="Secondary Language"
                    options={LANGUAGES}
                    value={secondaryLang}
                    onChange={setSecondaryLang}
                    placeholder="Select language (optional)"
                    enabledOptions={ENABLED_LANGUAGES}
                    onLockedClick={() => setShowEnterprisePopup(true)}
                  />
                </div>
              </div>

              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon name="Mic" size={13} className="text-violet-600" />
                  </div>
                  Agent Voice
                </h3>
                <p className="text-xs text-gray-500 mb-4">Choose a voice for your AI assistant. Click play to preview.</p>
                <audio ref={audioRef} className="hidden" onEnded={() => setPlayingVoice(null)} />
                {voicesLoading ? (
                  <div className="flex items-center justify-center py-10 gap-2">
                    <Icon name="Loader2" size={20} className="animate-spin text-violet-500" />
                    <span className="text-sm text-gray-500">Loading voices...</span>
                  </div>
                ) : voices.length === 0 ? (
                  <div className="flex items-center justify-center py-10">
                    <p className="text-sm text-gray-400">No voices available</p>
                  </div>
                ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {voices?.map((v) => {
                    const isSelected = selectedVoice === v?.id;
                    const isPlaying = playingVoice === v?.id;
                    return (
                      <button
                        key={v?.id}
                        onClick={() => setSelectedVoice(v?.id)}
                        className={`relative flex items-center gap-3 p-4 rounded-xl border-2 transition-all duration-150 text-left ${
                          isSelected
                            ? 'border-violet-400 bg-violet-50 shadow-md'
                            : 'border-gray-100 bg-white hover:border-violet-200 hover:shadow-sm'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${v?.color} flex items-center justify-center flex-shrink-0`}>
                          <span className="text-white font-bold text-sm">{v?.name?.[0]}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${isSelected ? 'text-violet-700' : 'text-gray-800'}`}>{v?.name}</p>
                          <p className="text-xs text-gray-400">{v?.gender} · {v?.accent}</p>
                        </div>
                        {v?.sampleUrl && (
                        <button
                          onClick={(e) => {
                            e?.stopPropagation();
                            if (isPlaying) {
                              audioRef.current?.pause();
                              setPlayingVoice(null);
                            } else {
                              if (audioRef.current) {
                                audioRef.current.src = v.sampleUrl;
                                audioRef.current.play().catch(() => {});
                              }
                              setPlayingVoice(v?.id);
                            }
                          }}
                          className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors flex-shrink-0 ${
                            isPlaying ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-500 hover:bg-violet-100 hover:text-violet-600'
                          }`}
                        >
                          <Icon name={isPlaying ? 'Square' : 'Play'} size={12} />
                        </button>
                        )}
                        {isSelected && (
                          <div className="absolute top-2 right-2 w-4 h-4 rounded-full bg-violet-500 flex items-center justify-center">
                            <Icon name="Check" size={9} className="text-white" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                )}
              </div>
            </div>
          )}

          {/* ===== STEP 2 ===== */}
          {step === 2 && (
            <div className="space-y-6">
              {/* Call Context */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon name="Phone" size={13} className="text-violet-600" />
                  </div>
                  Call Context
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Company Name <span className="text-red-500">*</span></label>
                    <input
                      value={companyName}
                      onChange={(e) => setCompanyName(e?.target?.value)}
                      placeholder="Enter your company name"
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Call Purpose <span className="text-red-500">*</span></label>
                    <textarea
                      value={callPurpose}
                      onChange={(e) => setCallPurpose(e?.target?.value)}
                      placeholder="Describe the purpose of this AI assistant's calls..."
                      rows={6}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all resize-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">Additional Information</label>
                    <textarea
                      value={additionalInfo}
                      onChange={(e) => setAdditionalInfo(e?.target?.value)}
                      placeholder="Any extra context, instructions, or guidelines for the assistant..."
                      rows={5}
                      className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Custom Variables */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon name="Code2" size={13} className="text-violet-600" />
                  </div>
                  Custom Variables
                </h3>
                <p className="text-xs text-gray-500 mb-3">Variables available during the call. Predefined ones cannot be removed.</p>
                <div className="flex flex-wrap gap-2 mb-3">
                  {variables?.map((v) => {
                    const isPredefined = v === 'callee_name' || v === 'mobile_number';
                    return (
                      <span
                        key={v}
                        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium ${
                          isPredefined ? 'bg-violet-100 text-violet-700' : 'bg-gray-100 text-gray-700'
                        }`}
                      >
                        <Icon name="Braces" size={11} />
                        {v}
                        {!isPredefined && (
                          <button onClick={() => handleRemoveVar(v)} className="ml-1 hover:text-red-500 transition-colors">
                            <Icon name="X" size={10} />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <input
                    value={newVar}
                    onChange={(e) => setNewVar(e?.target?.value?.replace(/[^a-zA-Z_]/g, ''))}
                    onKeyDown={(e) => e?.key === 'Enter' && handleAddVar()}
                    placeholder="Add variable name..."
                    className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-400 focus:border-transparent transition-all"
                  />
                  <button
                    onClick={handleAddVar}
                    disabled={!newVar?.trim()}
                    className="px-4 py-2 bg-violet-600 text-white rounded-xl text-sm font-medium hover:bg-violet-700 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Icon name="Plus" size={14} />
                    Add
                  </button>
                </div>
              </div>

              {/* Knowledge Base */}
              <div>
                <h3 className="text-base font-bold text-gray-900 mb-1 flex items-center gap-2">
                  <div className="w-6 h-6 rounded-lg bg-violet-100 flex items-center justify-center">
                    <Icon name="BookOpen" size={13} className="text-violet-600" />
                  </div>
                  Knowledge Base
                </h3>
                <p className="text-xs text-gray-500 mb-3">Attach knowledge sources for your assistant to reference during calls.</p>

                {/* Hidden file input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept=".pdf,.docx,.doc,.txt,.csv,.xls,.xlsx"
                  onChange={handleFileUpload}
                  className="hidden"
                />

                {/* Predefined KB Sections — 1:1 doc mapping */}
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Sections</p>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
                  {KB_SECTIONS?.map((sec) => {
                    const isActive = activeKBSections?.includes(sec?.id);
                    const fileName = sectionFiles[sec?.id];
                    return (
                      <div key={sec?.id} className="flex flex-col">
                        <button
                          onClick={() => toggleKBSection(sec?.id)}
                          className={`flex flex-col items-center gap-1.5 p-3 rounded-xl border-2 transition-all duration-150 ${
                            fileName
                              ? 'border-violet-400 bg-violet-50 shadow-sm'
                              : isActive
                              ? 'border-violet-400 bg-violet-50 shadow-sm'
                              : 'border-gray-100 bg-white hover:border-violet-200'
                          }`}
                        >
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive || fileName ? 'bg-violet-100' : 'bg-gray-100'}`}>
                            <Icon name={sec?.icon} size={15} className={isActive || fileName ? 'text-violet-600' : 'text-gray-500'} />
                          </div>
                          <span className={`text-xs font-medium text-center leading-tight ${isActive || fileName ? 'text-violet-700' : 'text-gray-600'}`}>{sec?.label}</span>
                        </button>
                        {isActive && (
                          <>
                            <button
                              onClick={() => { setUploadingSectionId(sec.id); fileInputRef.current?.click(); }}
                              className="mt-1.5 flex flex-col items-center justify-center gap-1 px-2 py-3 bg-gray-50 hover:bg-violet-50 rounded-lg border border-dashed border-gray-200 hover:border-violet-300 transition-colors"
                            >
                              <Icon name="Upload" size={14} className="text-gray-400" />
                              <span className="text-[10px] text-gray-500 font-medium">{fileName?.length > 0 ? 'Add more' : 'Upload docs'}</span>
                              <span className="text-[9px] text-gray-400">PDF, DOCX, TXT, CSV, XLS</span>
                            </button>
                            {fileName?.length > 0 && (
                              <div className="mt-1.5 space-y-1">
                                {fileName.map((f, fi) => (
                                  <div key={fi} className={`flex items-center gap-1 px-2 py-1 rounded-lg border ${
                                    f.status === 'error' ? 'bg-red-50 border-red-200' : 'bg-violet-50 border-violet-200'
                                  }`}>
                                    {f.status === 'uploading' ? (
                                      <Icon name="Loader2" size={10} className="text-violet-500 animate-spin flex-shrink-0" />
                                    ) : f.status === 'error' ? (
                                      <Icon name="AlertCircle" size={10} className="text-red-500 flex-shrink-0" />
                                    ) : (
                                      <Icon name="CheckCircle2" size={10} className="text-green-500 flex-shrink-0" />
                                    )}
                                    <span className={`text-[10px] truncate flex-1 ${f.status === 'error' ? 'text-red-600' : 'text-violet-700'}`}>{f.name}</span>
                                    {f.status !== 'uploading' && (
                                      <button onClick={(e) => { e.stopPropagation(); setSectionFiles((p) => { const updated = (p[sec.id] || []).filter((_, j) => j !== fi); if (updated.length === 0) { const n = {...p}; delete n[sec.id]; return n; } return { ...p, [sec.id]: updated }; }); }} className="flex-shrink-0">
                                        <Icon name="X" size={8} className="text-gray-400 hover:text-red-500" />
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ===== STEP 3 ===== */}
          {step === 3 && !otpVerified && (
            /* --- OTP Verification (shown until phone is verified) --- */
            <div className="flex flex-col items-center justify-center py-8 px-4">
              <div className="w-full max-w-md">
                <div className="mb-6">
                  <h3 className="text-2xl font-extrabold text-gray-900 leading-tight mb-2">
                    Activate your<br />AI Employee 🎉
                  </h3>
                  <p className="text-sm text-gray-500">
                    Verify your mobile number to complete your setup and start using Calltify.
                  </p>
                </div>

                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-800 mb-2">Mobile Number</label>
                  <div className="flex items-center">
                    <div className="flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-l-md bg-gray-50 cursor-default select-none">
                      <span className="text-sm font-semibold text-gray-800">+91</span>
                      <Icon name="ChevronDown" size={14} className="text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      value={mobileNumber}
                      onChange={(e) => setMobileNumber(e?.target?.value?.replace(/\D/g, '')?.slice(0, 10))}
                      placeholder="9876543210"
                      className="flex-1 px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 outline-none bg-white border border-l-0 border-gray-200 rounded-r-md focus:ring-2 focus:ring-violet-400 focus:border-violet-400 transition-all"
                      disabled={otpSent}
                    />
                  </div>
                </div>

                {otpError && (
                  <p className="text-xs text-red-500 mb-3">{otpError}</p>
                )}

                {!otpSent && (
                  <button
                    onClick={handleSendOtp}
                    disabled={mobileNumber?.length < 10 || otpLoading}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-violet-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
                  >
                    {otpLoading ? (
                      <><Icon name="Loader2" size={16} className="animate-spin" /> Sending...</>
                    ) : (
                      <>Send OTP <Icon name="ChevronRight" size={16} /></>
                    )}
                  </button>
                )}

                {otpSent && (
                  <div className="mt-2">
                    <label className="block text-sm font-semibold text-gray-800 mb-3">Enter OTP</label>
                    <p className="text-xs text-gray-500 mb-4">
                      OTP sent to +91 {mobileNumber?.replace(/(\d{5})(\d{5})/, '$1 $2')}
                      <button
                        onClick={() => { setOtpSent(false); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                        className="ml-2 text-violet-600 font-medium hover:underline"
                      >
                        Change
                      </button>
                    </p>
                    <div className="flex gap-3 mb-4">
                      {otp?.map((digit, i) => (
                        <input
                          key={i}
                          ref={(el) => (otpRefs.current[i] = el)}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleOtpChange(i, e?.target?.value)}
                          onKeyDown={(e) => handleOtpKeyDown(i, e)}
                          disabled={otpLoading}
                          className="w-full aspect-square text-center text-lg font-bold border-2 border-gray-200 rounded-xl focus:outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-200 transition-all text-gray-800 bg-white disabled:opacity-50"
                        />
                      ))}
                    </div>
                    {otpLoading && (
                      <div className="flex items-center justify-center gap-2 mb-4">
                        <Icon name="Loader2" size={14} className="animate-spin text-violet-500" />
                        <p className="text-xs text-gray-500">Verifying...</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between mb-4">
                      <p className="text-xs text-gray-400">Didn't receive OTP?</p>
                      <button
                        onClick={handleResendOtp}
                        disabled={otpLoading}
                        className="text-xs text-violet-600 font-medium hover:underline disabled:opacity-50"
                      >
                        Resend OTP
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-center gap-1.5 mt-2">
                  <Icon name="Lock" size={13} className="text-gray-400" />
                  <p className="text-xs text-gray-400">Your information is secure and encrypted</p>
                </div>
              </div>
            </div>
          )}

          {/* Review screen removed — create happens directly from step 2 (verified) or after OTP (step 3) */}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-4 border-t border-gray-100 bg-gray-50/50 flex-shrink-0">
          <button
            onClick={() => step > 1 ? setStep((s) => s - 1) : onClose()}
            className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
          >
            <Icon name="ChevronLeft" size={15} />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>

          <div className="flex items-center gap-2 sm:gap-3">
            {createError && (
              <p className="text-xs text-red-500 max-w-[120px] sm:max-w-xs truncate">{createError}</p>
            )}
            {step === 1 && (
              <button
                onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'basic-details-create-ai-assistant-next-clicked', { content_category: 'AgentCreationFlow' }); window.dataLayer?.push({ event: 'basic_details_next_clicked' }); setStep(2); }}
                disabled={!agentName?.trim() || !primaryLang}
                className="flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-violet-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <Icon name="ChevronRight" size={15} />
              </button>
            )}
            {step === 2 && otpVerified && (
              <button
                onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'call-context-create-ai-assistant-next-clicked', { content_category: 'AgentCreationFlow' }); window.dataLayer?.push({ event: 'call_context_next_clicked' }); handleCreate(); }}
                disabled={!companyName?.trim() || !callPurpose?.trim() || creating}
                className="flex items-center gap-2 px-5 sm:px-8 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-bold hover:from-violet-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? (
                  <>
                    <Icon name="Loader2" size={16} className="animate-spin" />
                    <span className="hidden sm:inline">Creating...</span>
                    <span className="sm:hidden">...</span>
                  </>
                ) : (
                  <>
                    <Icon name="Sparkles" size={16} />
                    <span className="hidden sm:inline">Create AI Assistant</span>
                    <span className="sm:hidden">Create</span>
                  </>
                )}
              </button>
            )}
            {step === 2 && !otpVerified && (
              <button
                onClick={() => { if (typeof fbq === 'function') fbq('trackCustom', 'call-context-create-ai-assistant-next-clicked', { content_category: 'AgentCreationFlow' }); window.dataLayer?.push({ event: 'call_context_next_clicked' }); setStep(3); }}
                disabled={!companyName?.trim() || !callPurpose?.trim()}
                className="flex items-center gap-2 px-5 sm:px-6 py-2.5 bg-gradient-to-r from-violet-600 to-indigo-600 text-white rounded-xl text-sm font-semibold hover:from-violet-700 hover:to-indigo-700 shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <Icon name="ChevronRight" size={15} />
              </button>
            )}
          </div>
        </div>
      </div>
      <EnterpriseContactPopup isOpen={showEnterprisePopup} onClose={() => setShowEnterprisePopup(false)} />
      <AgentLoadingScreen isOpen={showLoading} onComplete={handleLoadingComplete} />
    </div>
  );
};

export default AssistantCreationFlow;
export { EnterpriseContactPopup };
